import type {
  OdstopanjeKazalnika,
  OdstopanjePravila,
  SplosnaNastavitevKazalnika,
  SplosnaNastavitevPravila,
  SplosneNastavitveKazalnikov as Nastavitve,
} from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';
import { useBesedila, useJezik } from '../lib/jezik';

/** Da / ne / privzeto za izbirnik; privzeto je `null`. */
type Trojno = 'privzeto' | 'da' | 'ne';
const vTrojno = (v: boolean | null): Trojno => (v === null ? 'privzeto' : v ? 'da' : 'ne');
const izTrojnega = (v: Trojno): boolean | null => (v === 'privzeto' ? null : v === 'da');

/** Prazno polje pomeni privzeto. */
const aliNull = (v: string): string | null => (v.trim() === '' ? null : v);

/**
 * Število iz vnosa; decimalno ločilo je vejica (dovoljena je tudi pika).
 * Pragovi so majhna števila, zato brez ločil tisočic. Prazno je privzeto.
 */
function stevilo(v: string): number | null | 'napaka' {
  const t = v.trim();
  if (t === '') return null;
  if (!/^-?\d+([.,]\d+)?$/.test(t)) return 'napaka';
  // »1.000« je v slovenščini tisoč, ne ena: dvoumno, zato zavrnjeno.
  if (/^\d{1,3}\.\d{3}$/.test(t)) return 'napaka';
  return Number(t.replace(',', '.'));
}

/** Prag za prikaz: decimalna vejica, brez ločil tisočic. */
const zapisPraga = (v: number): string => String(v).replace('.', ',');

interface LastnostiGumbov {
  shranjujem: boolean;
  imaOdstopanje: boolean;
  naShrani: () => void;
  naPonastavi: () => void;
  napaka: string | null;
  shranjeno: boolean;
}

function Gumbi({ shranjujem, imaOdstopanje, naShrani, naPonastavi, napaka, shranjeno }: LastnostiGumbov): JSX.Element {
  const b = useBesedila();
  return (
    <>
      {napaka !== null && <p className="napaka">{napaka}</p>}
      <p className="gumbi">
        <button type="button" className="gumb" onClick={naShrani} disabled={shranjujem}>
          {shranjujem ? b.sn.shranjujem : b.sn.shrani}
        </button>
        <button type="button" className="tiho" onClick={naPonastavi} disabled={shranjujem || !imaOdstopanje}>
          {b.sn.ponastavi}
        </button>
        {shranjeno && <span className="uspeh-besedilo">{b.sn.shranjeno}</span>}
      </p>
    </>
  );
}

/** Skupno vedenje obrazca: shranjevanje, ponastavitev, napaka, osvežitev. */
function useObrazec(naSpremembo: () => void) {
  const b = useBesedila();
  const [shranjujem, setShranjujem] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjeno, setShranjeno] = useState(false);
  const izvedi = (delo: Promise<unknown>): void => {
    setShranjujem(true);
    setNapaka(null);
    setShranjeno(false);
    delo
      .then(() => {
        setShranjeno(true);
        naSpremembo();
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.sn.napakaShranjevanja);
      })
      .finally(() => {
        setShranjujem(false);
      });
  };
  return { shranjujem, napaka, shranjeno, setNapaka, izvedi };
}

function ObrazecKazalnika({
  k,
  naSpremembo,
}: {
  k: SplosnaNastavitevKazalnika;
  naSpremembo: () => void;
}): JSX.Element {
  const b = useBesedila();
  const jezik = useJezik();
  const o = k.odstopanje;
  const [vklop, setVklop] = useState<Trojno>(vTrojno(o?.vklopljen ?? null));
  const [vrstniRed, setVrstniRed] = useState(o?.vrstniRed === null || o === null ? '' : String(o.vrstniRed));
  const [nazivSl, setNazivSl] = useState(o?.nazivSl ?? '');
  const [nazivEn, setNazivEn] = useState(o?.nazivEn ?? '');
  const [opisSl, setOpisSl] = useState(o?.opisSl ?? '');
  const [opisEn, setOpisEn] = useState(o?.opisEn ?? '');
  const f = useObrazec(naSpremembo);

  const vklopljen = o?.vklopljen ?? k.privzeto.vklopljen;
  // Naziv v glavi kartice je v jeziku vmesnika, kot na strani Pregled.
  const naziv =
    jezik === 'en' ? (o?.nazivEn ?? k.privzeto.nazivEn) : (o?.nazivSl ?? k.privzeto.nazivSl);

  const shrani = (): void => {
    const red = stevilo(vrstniRed);
    if (red === 'napaka' || (red !== null && !Number.isInteger(red))) {
      f.setNapaka(b.sn.vrstniRedNapaka);
      return;
    }
    const odstopanje: OdstopanjeKazalnika = {
      vklopljen: izTrojnega(vklop),
      vrstniRed: red,
      nazivSl: aliNull(nazivSl),
      nazivEn: aliNull(nazivEn),
      opisSl: aliNull(opisSl),
      opisEn: aliNull(opisEn),
    };
    f.izvedi(api.shraniSplosniKazalnik(k.koda, odstopanje));
  };

  const polje = (napis: string, vrednost: string, namig: string, naSpremembo: (v: string) => void): JSX.Element => (
    <label className="polje">
      {napis}
      <input
        value={vrednost}
        placeholder={namig}
        onChange={(e) => {
          naSpremembo(e.target.value);
        }}
      />
    </label>
  );
  const besedilo = (napis: string, vrednost: string, namig: string, naSpremembo: (v: string) => void): JSX.Element => (
    <label className="polje">
      {napis}
      <textarea
        rows={3}
        value={vrednost}
        placeholder={namig}
        onChange={(e) => {
          naSpremembo(e.target.value);
        }}
      />
    </label>
  );

  return (
    <details className="kartica nastavitev-kazalnika">
      <summary>
        <strong>{naziv}</strong> <span className="drobno">· {k.nazivSkupine}</span>
        {!vklopljen && <span className="oznaka-stanja">{b.sn.izklopljen}</span>}
        {o !== null && <span className="oznaka-stanja prilagojeno">{b.sn.prilagojeno}</span>}
        <span className="oznaka-stanja motor">{k.motor === 'formula' ? b.sn.motorFormula : b.sn.motorVgrajen}</span>
      </summary>
      <p className="drobno">
        <strong>{b.sn.formula}:</strong> {k.formula} ({k.izGraditelja ? b.sn.izGraditelja : b.sn.formulaNiUrejljiva})
      </p>
      <GumbiGraditelja k={k} naziv={naziv} naSpremembo={naSpremembo} />
      <div className="mreza-obrazca">
        <label className="polje">
          {b.sn.vklopZaVse}
          <select
            value={vklop}
            onChange={(e) => {
              setVklop(e.target.value as Trojno);
            }}
          >
            <option value="privzeto">{b.sn.privzetoVklopljen(k.privzeto.vklopljen)}</option>
            <option value="da">{b.sn.vklopljen}</option>
            <option value="ne">{b.sn.izklopljenIzbira}</option>
          </select>
        </label>
        <label className="polje">
          {b.sn.vrstniRed}
          <input
            inputMode="numeric"
            value={vrstniRed}
            placeholder={String(k.privzeto.vrstniRed)}
            onChange={(e) => {
              setVrstniRed(e.target.value);
            }}
          />
        </label>
        {polje(b.sn.nazivSl, nazivSl, k.privzeto.nazivSl, setNazivSl)}
        {polje(b.sn.nazivEn, nazivEn, k.privzeto.nazivEn, setNazivEn)}
      </div>
      {besedilo(b.sn.opisSl, opisSl, k.privzeto.opisSl, setOpisSl)}
      {besedilo(b.sn.opisEn, opisEn, k.privzeto.opisEn, setOpisEn)}
      <Gumbi
        shranjujem={f.shranjujem}
        imaOdstopanje={o !== null}
        naShrani={shrani}
        naPonastavi={() => {
          f.izvedi(api.ponastaviSplosniKazalnik(k.koda));
        }}
        napaka={f.napaka}
        shranjeno={f.shranjeno}
      />
    </details>
  );
}

/**
 * Pot v graditelj (6.3): kazalnik iz graditelja se uredi ali izbriše,
 * vgrajeni se kopira v nov kazalnik s formulo (vgrajeni izračun ostane).
 */
function GumbiGraditelja({
  k,
  naziv,
  naSpremembo,
}: {
  k: SplosnaNastavitevKazalnika;
  naziv: string;
  naSpremembo: () => void;
}): JSX.Element | null {
  const b = useBesedila();
  const [potrdi, setPotrdi] = useState(false);
  const [brisem, setBrisem] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  if (k.formulaZapis === null) return null;
  const pot = `/nastavitve/kazalniki/graditelj?${k.izGraditelja ? 'uredi' : 'kopija'}=${encodeURIComponent(k.koda)}`;
  const izbrisi = (): void => {
    setBrisem(true);
    setNapaka(null);
    api
      .izbrisiDefinicijo(k.koda)
      .then(naSpremembo)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.sn.napakaShranjevanja);
      })
      .finally(() => {
        setBrisem(false);
      });
  };
  return (
    <div className="gumbi-graditelja">
      <Link to={pot} className="gumb" title={k.izGraditelja ? undefined : b.sn.kopirajNamig}>
        {k.izGraditelja ? b.sn.urediVGraditelju : b.sn.kopiraj}
      </Link>
      {k.izGraditelja &&
        (potrdi ? (
          <span className="potrdi-izbris" role="alert">
            {b.sn.izbrisiPotrdi(naziv)}{' '}
            <button type="button" className="gumb nevaren" onClick={izbrisi} disabled={brisem}>
              {b.sn.izbrisiDa}
            </button>{' '}
            <button
              type="button"
              className="tiho"
              onClick={() => {
                setPotrdi(false);
              }}
            >
              {b.preklici}
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="tiho"
            onClick={() => {
              setPotrdi(true);
            }}
          >
            {b.sn.izbrisi}
          </button>
        ))}
      {napaka !== null && <p className="napaka">{napaka}</p>}
    </div>
  );
}

function ObrazecPravila({
  p,
  izkljuci,
  naSpremembo,
}: {
  p: SplosnaNastavitevPravila;
  /** Pravila, ki jih to pravilo izključi, kadar se sproži. */
  izkljuci: string[];
  naSpremembo: () => void;
}): JSX.Element {
  const b = useBesedila();
  const jezik = useJezik();
  const o = p.odstopanje;
  const [vklop, setVklop] = useState<Trojno>(vTrojno(o?.vklopljeno ?? null));
  const [stopnja, setStopnja] = useState<string>(o?.stopnja ?? 'privzeto');
  const [pragovi, setPragovi] = useState<Record<string, string>>(
    Object.fromEntries(
      Object.keys(p.privzeto.pragovi).map((k) => {
        const v = o?.pragovi[k];
        return [k, v === null || v === undefined ? '' : zapisPraga(v)];
      }),
    ),
  );
  const [naslovSl, setNaslovSl] = useState(o?.naslovSl ?? '');
  const [naslovEn, setNaslovEn] = useState(o?.naslovEn ?? '');
  const [besediloSl, setBesediloSl] = useState(o?.besediloSl ?? '');
  const [besediloEn, setBesediloEn] = useState(o?.besediloEn ?? '');
  const f = useObrazec(naSpremembo);

  const vklopljeno = o?.vklopljeno ?? p.privzeto.vklopljeno;
  const naslov =
    jezik === 'en' ? (o?.naslovEn ?? p.privzeto.naslovEn) : (o?.naslovSl ?? p.privzeto.naslovSl);
  const opisPraga = (k: string): string => b.sn.pragovi[k] ?? k;

  const shrani = (): void => {
    const vrednosti: Record<string, number | null> = {};
    for (const [k, v] of Object.entries(pragovi)) {
      const n = stevilo(v);
      if (n === 'napaka') {
        f.setNapaka(b.sn.pragNapaka(opisPraga(k)));
        return;
      }
      vrednosti[k] = n;
    }
    const odstopanje: OdstopanjePravila = {
      vklopljeno: izTrojnega(vklop),
      stopnja: stopnja === 'warning' || stopnja === 'info' ? stopnja : null,
      pragovi: vrednosti,
      naslovSl: aliNull(naslovSl),
      naslovEn: aliNull(naslovEn),
      besediloSl: aliNull(besediloSl),
      besediloEn: aliNull(besediloEn),
    };
    f.izvedi(api.shraniSplosnoPravilo(p.koda, odstopanje));
  };

  const polje = (napis: string, vrednost: string, namig: string, naSpremembo: (v: string) => void): JSX.Element => (
    <label className="polje">
      {napis}
      <input
        value={vrednost}
        placeholder={namig}
        onChange={(e) => {
          naSpremembo(e.target.value);
        }}
      />
    </label>
  );
  const besedilo = (napis: string, vrednost: string, namig: string, naSpremembo: (v: string) => void): JSX.Element => (
    <label className="polje">
      {napis}
      <textarea
        rows={4}
        value={vrednost}
        placeholder={namig}
        onChange={(e) => {
          naSpremembo(e.target.value);
        }}
      />
    </label>
  );

  return (
    <details className="kartica nastavitev-pravila">
      <summary>
        <code>{p.koda}</code> <strong>{naslov}</strong>{' '}
        <span className="drobno">· {p.vrsta === 'trend' ? b.sn.vrstaTrend : b.sn.vrstaRaven}</span>
        {!vklopljeno && <span className="oznaka-stanja">{b.sn.izklopljeno}</span>}
        {o !== null && <span className="oznaka-stanja prilagojeno">{b.sn.prilagojeno}</span>}
      </summary>
      {/* Povezave med pravili: samo prikaz, logika je v nastavitvah pravil. */}
      {(p.izkljucijo.length > 0 || izkljuci.length > 0) && (
        <ul className="povezave-pravil drobno">
          {p.izkljucijo.length > 0 && <li>{b.sn.izkljuciGa(p.izkljucijo.join(', '))}</li>}
          {izkljuci.length > 0 && <li>{b.sn.izkljuci(izkljuci.join(', '))}</li>}
        </ul>
      )}
      <div className="mreza-obrazca">
        <label className="polje">
          {b.sn.vklop}
          <select
            value={vklop}
            onChange={(e) => {
              setVklop(e.target.value as Trojno);
            }}
          >
            <option value="privzeto">{b.sn.privzetoVklopljeno(p.privzeto.vklopljeno)}</option>
            <option value="da">{b.sn.vklopljeno}</option>
            <option value="ne">{b.sn.izklopljenoIzbira}</option>
          </select>
        </label>
        <label className="polje">
          {b.sn.stopnja}
          <select
            value={stopnja}
            onChange={(e) => {
              setStopnja(e.target.value);
            }}
          >
            <option value="privzeto">{b.sn.privzetoStopnja(p.privzeto.stopnja)}</option>
            <option value="warning">warning</option>
            <option value="info">info</option>
          </select>
        </label>
        {Object.entries(p.privzeto.pragovi).map(([k, privzeto]) => (
          <label key={k} className="polje">
            {opisPraga(k)}
            <input
              inputMode="decimal"
              value={pragovi[k] ?? ''}
              placeholder={zapisPraga(privzeto)}
              onChange={(e) => {
                setPragovi({ ...pragovi, [k]: e.target.value });
              }}
            />
          </label>
        ))}
      </div>
      <div className="mreza-obrazca">
        {polje(b.sn.naslovSl, naslovSl, p.privzeto.naslovSl, setNaslovSl)}
        {polje(b.sn.naslovEn, naslovEn, p.privzeto.naslovEn, setNaslovEn)}
      </div>
      <p className="namig">
        {b.sn.nadomestki(p.nadomestki.map((n) => `{${n}}`).join(', ') || b.sn.brezNadomestkov)}
      </p>
      {besedilo(b.sn.besediloSl, besediloSl, p.privzeto.besediloSl, setBesediloSl)}
      {besedilo(b.sn.besediloEn, besediloEn, p.privzeto.besediloEn, setBesediloEn)}
      <Gumbi
        shranjujem={f.shranjujem}
        imaOdstopanje={o !== null}
        naShrani={shrani}
        naPonastavi={() => {
          f.izvedi(api.ponastaviSplosnoPravilo(p.koda));
        }}
        napaka={f.napaka}
        shranjeno={f.shranjeno}
      />
    </details>
  );
}

/**
 * Splošne nastavitve → Kazalniki (docs/ARHITEKTURA.md, 6.3).
 *
 * Velja za vsa podjetja. Prazno polje pomeni privzeto vrednost iz aplikacije;
 * shranijo se samo odstopanja, zato jih posodobitev aplikacije ne povozi.
 * Podjetje lahko kazalnik zase vklopi ali izklopi v Nastavitvah kazalnikov.
 * Stran je prevedena (skrbnik ureja tudi angleška besedila).
 */
export function SplosneNastavitveKazalnikov(): JSX.Element {
  const b = useBesedila();
  const [nastavitve, setNastavitve] = useState<Nastavitve | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  // Ključ osvežitve: po shranjevanju se obrazci napolnijo s shranjenim stanjem.
  const [razlicica, setRazlicica] = useState(0);

  const osvezi = (): void => {
    api
      .splosneNastavitveKazalnikov()
      .then((n) => {
        setNastavitve(n);
        setRazlicica((r) => r + 1);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.sn.napakaBranja);
      });
  };
  useEffect(osvezi, []);

  const pravila = nastavitve?.pravila ?? [];
  /** Obratna smer izključevanja: katera pravila to pravilo izključi. */
  const izkljuci = (koda: string): string[] =>
    pravila.filter((p) => p.izkljucijo.includes(koda)).map((p) => p.koda);
  const poKazalnikih = (nastavitve?.kazalniki ?? []).map((k) => ({
    k,
    pravila: pravila.filter((p) => p.kazalnik === k.koda),
  }));

  return (
    <main className="siroko">
      <div className="vsebina">
        <nav className="drobtine">
          <span>{b.sn.splosneNastavitve}</span>
        </nav>
        <h1>{b.sn.naslov}</h1>
        <p className="podnaslov">{b.sn.podnaslov}</p>
        <p>
          <Link to="/nastavitve/kazalniki/graditelj" className="gumb">
            {b.sn.ustvari}
          </Link>
        </p>
        {napaka !== null && <p className="napaka">{napaka}</p>}
        {nastavitve === null && napaka === null && <p>{b.sn.beremNastavitve}</p>}

        {poKazalnikih.map(({ k, pravila: pravilaKazalnika }) => (
          <section key={`${k.koda}-${String(razlicica)}`} className="skupina-nastavitev">
            <ObrazecKazalnika k={k} naSpremembo={osvezi} />
            {pravilaKazalnika.length > 0 && (
              <div className="pravila-kazalnika">
                <h3 className="drobno">{b.sn.pravilaOpozoril}</h3>
                {pravilaKazalnika.map((p) => (
                  <ObrazecPravila key={p.koda} p={p} izkljuci={izkljuci(p.koda)} naSpremembo={osvezi} />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </main>
  );
}
